import { signIn, SignInResponse } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { Routes } from "../../../../routes";
import React, { useState } from "react";
import { useUser } from "~/modules/user/hooks/useUser";
import * as Analytics from "../../analytics";
import { LoadingBoundary } from "~/components/LoadingBoundary";
import { getEmailVerifyRedirectUrl } from "../../util";
import { isSignInResponseOk } from "../../util/isAuthResponseOk";
import { AuthPageLayout } from "../../components/AuthPageLayout";
import { OAuthButton } from "../../components/OAuthButton";
import { EmailForm } from "../../components/EmailForm";
import {
    AuthFormCard,
    AuthFormCardSeparator,
} from "../../components/AuthFormCard";
import { LoggedInCard } from "../../components/LoggedInCard";

// // // //

function LoginCard() {
    const router = useRouter();
    const searchParams = useSearchParams();

    // Track loading state of email form submission
    const [loadingAction, setLoadingAction] = useState<
        "email" | "google" | null
    >(null);

    // Handle callbackUrl query parameter
    const callbackUrl = searchParams?.get("callbackUrl") ?? undefined;

    // Callback function for email form submission + redirects
    async function submitEmailForm(email: string) {
        // Track email login event
        Analytics.trackEmailLogin({ email });

        // Set loading state
        setLoadingAction("email");

        // Submit email form
        const response: SignInResponse | undefined = await signIn("email", {
            email,
            redirect: false,
        });

        // Handle error if there is no response
        // (it could potentially be `undefined`)
        if (!response) {
            Analytics.trackEmailLoginResponseUndefined({ email });
            return;
        }

        // Check if response is ok
        const isResponseOk = isSignInResponseOk(response);

        // Handle OTP email error
        if (!isResponseOk) {
            // Capture error event
            Analytics.trackEmailLoginResponseOkFalse({ email });

            // Redirect to login page with error
            router.replace(Routes.authError);
            return;
        }

        // Email sent successfully -> redirect to verify page
        Analytics.trackEmailLoginSuccess({ email });
        router.replace(getEmailVerifyRedirectUrl({ email }));
    }

    const disabled = loadingAction !== null;

    return (
        <AuthFormCard variant="login">
            <EmailForm
                disabled={disabled}
                onSubmit={submitEmailForm}
                showLoading={loadingAction === "email"}
                variant="login"
            />

            <AuthFormCardSeparator />

            <OAuthButton
                loading={loadingAction === "google"}
                disabled={disabled}
                provider="google"
                variant="login"
                onClick={() => {
                    Analytics.trackGoogleLogin();
                    signIn("google", {
                        redirect: true,
                        callbackUrl,
                    });
                }}
            />
        </AuthFormCard>
    );
}

export function LoginPage() {
    const { user, isLoggedIn, loading } = useUser();

    return (
        <LoadingBoundary className="h-full" loading={loading}>
            <AuthPageLayout>
                {!isLoggedIn && <LoginCard />}
                {isLoggedIn && (
                    <LoggedInCard email={user.email || ""} variant="login" />
                )}
            </AuthPageLayout>
        </LoadingBoundary>
    );
}
